import type { SxProps, Theme } from '@mui/material';

export const WEBVIEWER_LICENSE =
	'demo:1770900361581:6082a73403000000008bfdc578db599d0904b024b2339e144c1e745ec5';

/**
 * Toolbar profiles for Apryse WebViewer.
 * `documentSigning` is shared by Onboarding document review, manager attestation, and Banquet Event Order fill/edit.
 */
export type WebViewerToolbarProfile = 'formFill' | 'signOnly' | 'viewOnly' | 'documentSigning';

/** Persists drawn/uploaded signatures across document switches (onboarding + banquet editors). */
export const DOCUMENT_SIGNING_SIGNATURES_STORAGE_KEY = 'onboarding-saved-signatures';

/** @deprecated Use DOCUMENT_SIGNING_SIGNATURES_STORAGE_KEY */
export const ONBOARDING_SIGNATURES_STORAGE_KEY = DOCUMENT_SIGNING_SIGNATURES_STORAGE_KEY;

/** Default document zoom (150%) applied when a PDF loads (non-documentSigning profiles). */
export const DEFAULT_WEBVIEWER_ZOOM = 1.5;

type WebViewerDefaultFitMode = 'zoom' | 'fitWidth';

const PROFILE_FIT_MODE: Record<WebViewerToolbarProfile, WebViewerDefaultFitMode> = {
	formFill: 'zoom',
	signOnly: 'zoom',
	viewOnly: 'zoom',
	documentSigning: 'fitWidth',
};

const COMMON_HIDDEN_TOOLBAR_GROUPS = [
	'toolbarGroup-Annotate',
	'toolbarGroup-Shapes',
	'toolbarGroup-Measure',
	'toolbarGroup-Edit',
	'toolbarGroup-EditText',
	'toolbarGroup-Forms',
	'toolbarGroup-Redact',
	'toolbarGroup-oe-Home',
	'toolbarGroup-oe-Insert',
	'toolbarGroup-oe-Layout',
	'toolbarGroup-oe-Review',
];

const PROFILE_UI: Record<
	WebViewerToolbarProfile,
	{
		disable: string[];
		enable: string[];
		toolbarGroup: string;
		toolMode: string;
	}
> = {
	formFill: {
		disable: COMMON_HIDDEN_TOOLBAR_GROUPS,
		enable: ['toolbarGroup-View', 'toolbarGroup-Insert', 'toolbarGroup-FillAndSign'],
		toolbarGroup: 'toolbarGroup-View',
		toolMode: 'Pan',
	},
	signOnly: {
		disable: [
			...COMMON_HIDDEN_TOOLBAR_GROUPS,
			'toolbarGroup-View',
			'viewControlsButton',
			'searchButton',
			'menuButton',
			'leftPanelButton',
			'toggleNotesButton',
		],
		enable: [
			'toolbarGroup-FillAndSign',
			'signatureToolButton',
			'freeHandToolButton',
			'freeTextToolButton',
		],
		toolbarGroup: 'toolbarGroup-FillAndSign',
		toolMode: 'Pan',
	},
	viewOnly: {
		disable: [
			...COMMON_HIDDEN_TOOLBAR_GROUPS,
			'toolbarGroup-Insert',
			'toolbarGroup-FillAndSign',
			'viewControlsButton',
			'searchButton',
			'menuButton',
			'leftPanelButton',
			'toggleNotesButton',
		],
		enable: ['toolbarGroup-View'],
		toolbarGroup: 'toolbarGroup-View',
		toolMode: 'Pan',
	},
	/** Signature + image stamp only — used by Onboarding, manager attestation, and Banquet Event Order editors. */
	documentSigning: {
		disable: [
			...COMMON_HIDDEN_TOOLBAR_GROUPS,
			'toolbarGroup-View',
			'viewControlsButton',
			'searchButton',
			'menuButton',
			'leftPanelButton',
			'toggleNotesButton',
			'zoom-container',
			'selectToolButton',
			'annotationEditToolButton',
			// Fill & Sign — keep signature only
			'freeTextToolButton',
			'crossStampToolButton',
			'checkStampToolButton',
			'dotStampToolButton',
			'rubberStampToolButton',
			'calendarToolButton',
			'dateFreeTextToolButton',
			// Insert — keep image stamp only (signature also appears here via shared element)
			'fileAttachmentButton',
			'fileAttachmentToolButton',
			// Other tools that may appear in ribbon flyouts
			'freeHandToolButton',
			'signatureFieldToolButton',
			'addImageContentToolButton',
			'addImageContentToolGroupButton',
			'stylePanelToggle',
			'defaultAnnotationUtilities',
		],
		enable: [
			'toolbarGroup-FillAndSign',
			'toolbarGroup-Insert',
			'signatureCreateToolButton',
			'stampToolButton',
			'panToolButton',
		],
		toolbarGroup: 'toolbarGroup-FillAndSign',
		toolMode: 'Pan',
	},
};

export function getWebViewerInitOptions(
	profile: WebViewerToolbarProfile,
	documentUrl?: string
): Record<string, unknown> {
	return {
		path: '/webviewer',
		licenseKey: WEBVIEWER_LICENSE,
		initialDoc: documentUrl || undefined,
		fullAPI: true,
		disableStreaming: true,
		disabledElements: PROFILE_UI[profile].disable,
	};
}

export function configureWebViewerUI(inst: any, profile: WebViewerToolbarProfile): void {
	const { disable, enable, toolbarGroup, toolMode } = PROFILE_UI[profile];
	inst.UI.disableElements(disable);
	inst.UI.enableElements(enable);

	if (profile === 'documentSigning') {
		trimDocumentSigningToolbarGroups(inst);
		applyDocumentSigningPanTool(inst);
		return;
	}

	inst.UI.setToolbarGroup(toolbarGroup);
	inst.UI.setToolMode(toolMode);
}

/** Pan/hand tool active by default — View ribbon tab stays hidden. */
function applyDocumentSigningPanTool(inst: any): void {
	const { documentViewer } = inst.Core;
	const panTool = documentViewer.getTool('Pan');
	if (panTool) {
		documentViewer.setToolMode(panTool);
	}
	inst.UI.setToolMode('Pan');
}

/** Limit ribbon groups to signature (Fill & Sign) and image stamp (Insert) only. */
function trimDocumentSigningToolbarGroups(inst: any): void {
	try {
		const fillAndSign = inst.UI.getGroupedItems?.('fillAndSignGroupedItems');
		if (fillAndSign?.setItems) {
			fillAndSign.setItems(['signatureCreateToolButton']);
		}
		const insertTools = inst.UI.getGroupedItems?.('insertToolsGroupedItems');
		if (insertTools?.setItems) {
			insertTools.setItems(['stampToolButton']);
		}
	} catch (error) {
		console.error('[WebViewer] Failed to trim documentSigning toolbar groups:', error);
	}
}

/** PDF open actions can force FitPage and override our zoom — ignore those on document open. */
export function ignoreDocumentOpenFitActions(inst: any): void {
	if (inst.__openActionPatchApplied) return;

	try {
		const { documentViewer } = inst.Core;
		const Actions = inst.Core?.Actions ?? inst.Actions;
		if (!Actions?.GoTo?.prototype?.onTriggered) return;

		const originalOnTriggered = Actions.GoTo.prototype.onTriggered;
		Actions.GoTo.prototype.onTriggered = function onTriggeredPatched(
			this: unknown,
			target: unknown,
			event: { name?: string },
			...args: unknown[]
		) {
			if (target === documentViewer.getDocument() && event?.name === 'Open') {
				return;
			}
			return originalOnTriggered.apply(this, [target, event, ...args]);
		};
		inst.__openActionPatchApplied = true;
	} catch {
		/* non-fatal */
	}
}

export function applyWebViewerDefaultZoom(inst: any, profile: WebViewerToolbarProfile = 'formFill'): void {
	const { UI } = inst;
	const { documentViewer } = inst.Core;
	const fitMode = PROFILE_FIT_MODE[profile];

	if (fitMode === 'fitWidth') {
		UI.setFitMode(UI.FitMode.FitWidth);
		if (documentViewer?.setFitMode && documentViewer.FitMode?.FitWidth) {
			documentViewer.setFitMode(documentViewer.FitMode.FitWidth);
		}
		return;
	}

	UI.setFitMode(UI.FitMode.Zoom);
	if (documentViewer?.setFitMode && documentViewer.FitMode?.Zoom) {
		documentViewer.setFitMode(documentViewer.FitMode.Zoom);
	}
	UI.setZoomLevel(DEFAULT_WEBVIEWER_ZOOM);
}

export function applyWebViewerDefaultTool(inst: any, profile: WebViewerToolbarProfile): void {
	if (profile === 'documentSigning') {
		applyDocumentSigningPanTool(inst);
		return;
	}
	inst.UI.setToolMode(PROFILE_UI[profile].toolMode);
}

function scheduleDefaultView(inst: any, profile: WebViewerToolbarProfile): void {
	const expectedFitMode =
		PROFILE_FIT_MODE[profile] === 'fitWidth' ? inst.UI.FitMode.FitWidth : inst.UI.FitMode.Zoom;

	const applyView = () => {
		try {
			applyWebViewerDefaultZoom(inst, profile);
			applyWebViewerDefaultTool(inst, profile);
		} catch (error) {
			console.error('[WebViewer] Failed to apply default view:', error);
		}
	};

	const onFitModeUpdated = () => {
		try {
			if (inst.UI.getFitMode?.() !== expectedFitMode) {
				applyView();
			}
		} catch {
			/* non-fatal */
		}
	};

	inst.Core.documentViewer.addEventListener('fitModeUpdated', onFitModeUpdated);
	window.setTimeout(() => {
		inst.Core.documentViewer.removeEventListener('fitModeUpdated', onFitModeUpdated);
	}, 2000);

	applyView();
	requestAnimationFrame(applyView);
	[100, 300, 600, 1000].forEach((delayMs) => window.setTimeout(applyView, delayMs));
}

export function setupWebViewerOnDocumentLoaded(inst: any, profile: WebViewerToolbarProfile): void {
	configureWebViewerUI(inst, profile);
	scheduleDefaultView(inst, profile);
}

/** Call as soon as the WebViewer instance is created (before or after the first document loads). */
export function prepareWebViewerInstance(inst: any, profile: WebViewerToolbarProfile): void {
	ignoreDocumentOpenFitActions(inst);
	configureWebViewerUI(inst, profile);
	if (profile === 'documentSigning') {
		setupWebViewerSignaturePersistence(inst);
	}
}

/** Persist saved signatures across document switches (onboarding + banquet). */
export function setupWebViewerSignaturePersistence(
	inst: any,
	storageKey: string = DOCUMENT_SIGNING_SIGNATURES_STORAGE_KEY
): void {
	if (inst.__signaturePersistenceApplied) return;
	inst.__signaturePersistenceApplied = true;

	const { documentViewer } = inst.Core;
	const signatureTool = documentViewer.getTool('AnnotationCreateSignature');
	if (!signatureTool) return;

	let isRestoring = false;

	const persistSignatures = async () => {
		if (isRestoring) return;
		try {
			const exported = await signatureTool.exportSignatures();
			localStorage.setItem(storageKey, JSON.stringify(exported));
		} catch (error) {
			console.error('[WebViewer] Failed to persist signatures:', error);
		}
	};

	const restoreSignatures = async () => {
		if (isRestoring) return;
		try {
			const saved = localStorage.getItem(storageKey);
			if (!saved) return;
			const signatures = JSON.parse(saved);
			if (!Array.isArray(signatures) || signatures.length === 0) return;

			// importSignatures appends; clear first so document switches don't duplicate the list.
			// Suppress persist while restoring so deleteAllSavedSignatures doesn't wipe storage.
			isRestoring = true;
			try {
				if (typeof signatureTool.deleteAllSavedSignatures === 'function') {
					signatureTool.deleteAllSavedSignatures();
				}
				await signatureTool.importSignatures(signatures);
			} finally {
				isRestoring = false;
			}
		} catch (error) {
			isRestoring = false;
			console.error('[WebViewer] Failed to restore signatures:', error);
		}
	};

	signatureTool.addEventListener('signatureSaved', persistSignatures);
	signatureTool.addEventListener('signatureDeleted', persistSignatures);
	documentViewer.addEventListener('documentLoaded', restoreSignatures);
}

/** @deprecated Use applyWebViewerDefaultZoom */
export function applyWebViewerSinglePageView(inst: any): void {
	applyWebViewerDefaultZoom(inst);
}

/** Fixed height sized to show roughly one PDF page — avoids full-viewport editors that break iPad scroll/sign dialogs. */
export function getWebViewerHeight(
	isTabletViewport: boolean,
	variant: 'inline' | 'modal' | 'onboardingInline' = 'inline'
): string {
	if (variant === 'modal') {
		return isTabletViewport ? '480px' : '560px';
	}
	if (variant === 'onboardingInline') {
		return isTabletViewport ? 'calc(100vh - 200px)' : 'calc(100vh - 260px)';
	}
	return isTabletViewport ? '500px' : '600px';
}

export function getWebViewerContainerSx(
	isTabletViewport: boolean,
	variant: 'inline' | 'modal' | 'onboardingInline' = 'inline'
): SxProps<Theme> {
	return {
		height: getWebViewerHeight(isTabletViewport, variant),
		width: '100%',
		border: '1px solid #e0e0e0',
		borderRadius: 1,
		overflow: 'hidden',
		position: 'relative',
		touchAction: 'manipulation',
		WebkitOverflowScrolling: 'touch',
	};
}
